import deleteStudent from '@/app/actions/students/deleteStudent'
import { updateStudentStatus } from '@/app/actions/students/updateStudentStatus'
import { useToast } from '@/hooks/use-toast'
import { isSuccessResult } from '@/lib/utils'
import { Prisma, StudentStatus } from '@prisma/client'
import {
  ArrowLeftRight,
  Bolt,
  Calendar,
  CircleDollarSign,
  DollarSign,
  Edit,
  LucideEllipsis,
  NotebookPen,
  Trash,
  View,
} from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'
import { Button } from '../button'
import ControlledConfirmDeleteDialog from '../dialogs/ControlledConfirmDeleteDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../dropdown-menu'

export default function StudentMenu({
  student,
}: {
  student: Prisma.StudentGetPayload<{
    include: { studentGroup: { include: { group: true } } }
  }>
}) {
  const [open, onOpenChange] = useState(false)
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const _deleteStudent = async () => {
    setLoading(true)
    const _response = await deleteStudent(student.studentId)
    if (isSuccessResult(_response)) {
      onOpenChange(false)
    } else {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    }
    setLoading(false)
  }

  const _updateStudentStatus = async (status: StudentStatus) => {
    setLoading(true)
    const response = await updateStudentStatus(student.studentId, status)
    if (isSuccessResult(response)) {
    } else {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    }
    setLoading(false)
  }

  return (
    <>
      <ControlledConfirmDeleteDialog
        open={open}
        onOpenChange={() => onOpenChange(!open)}
        text={`This action cannot be undone. This will permanently delete this student as well as any transactions, lessons, and notes associated with this student.`}
        loading={loading}
        serverAction={() => _deleteStudent()}
        confirmationMatchText={`${student.firstName} ${student.lastName}`}
        confirmation={true}
        confirmationText="Please enter the student's full name to confirm delete."
      />
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild className="">
          <Button variant="ghost" size="icon">
            <LucideEllipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>
            {student.firstName} {student.lastName}
          </DropdownMenuLabel>

          <Link href={`/accounts/${student.accountId}`}>
            <DropdownMenuItem>
              <View />
              View Account
            </DropdownMenuItem>
          </Link>
          <Link href={`/notes?studentId=${student.studentId}`}>
            <DropdownMenuItem>
              <NotebookPen />
              View Notes
            </DropdownMenuItem>
          </Link>

          <Link href={`/lessons/add?studentId=${student.studentId}`}>
            <DropdownMenuItem>
              <Calendar />
              Add Lesson
            </DropdownMenuItem>
          </Link>

          {/* Add Transaction Sub-menu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ArrowLeftRight />
              Add Transaction
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <Link
                href={`/transactions/add?accountId=${student.accountId}&type=payment`}
              >
                <DropdownMenuItem>
                  <DollarSign /> Payment
                </DropdownMenuItem>
              </Link>
              <Link
                href={`/transactions/add?accountId=${student.accountId}&type=charge`}
              >
                <DropdownMenuItem>
                  <CircleDollarSign /> Charge
                </DropdownMenuItem>
              </Link>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Bolt />
              Set Status
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {student.status === StudentStatus.INACTIVE && (
                <DropdownMenuItem
                  onClick={() => {
                    _updateStudentStatus(StudentStatus.ACTIVE)
                  }}
                >
                  Active
                </DropdownMenuItem>
              )}

              {student.status === StudentStatus.ACTIVE && (
                <DropdownMenuItem
                  onClick={() => {
                    _updateStudentStatus(StudentStatus.INACTIVE)
                  }}
                >
                  Inactive
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <Link href={`/students/edit/${student.studentId}`}>
            <DropdownMenuItem>
              <Edit />
              Edit Student
            </DropdownMenuItem>
          </Link>

          <DropdownMenuItem onClick={() => onOpenChange(!open)}>
            <Trash />
            Delete Student
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
